import { activityTypes } from "@constants/activities";

// Filter types for attendee detail view
export const ATTENDEE_FILTERS = {
  ALL: "all",
  ANSWERED: "answered",
  INCORRECT: "incorrect",
  WAITING: "waiting",
  VIEWED: "viewed",
};

// Status types for attendee cards
export const ATTENDEE_STATUS = {
  ANSWERED_CORRECT: "answered_correct",
  ANSWERED_INCORRECT: "answered_incorrect",
  WAITING: "waiting",
  VIEWED: "viewed", // For send activities - note viewed
  OPENED: "opened", // For send activities - link clicked
};

/**
 * Determines the attendee status based on activity type and response
 * @param {Object} attendee - The attendee object with user and response data
 * @param {string} activityType - The type of activity (poll, quiz, send)
 * @param {Array} options - The activity options
 * @returns {string} - The attendee status
 */
export const getAttendeeStatus = (attendee, activityType, options = []) => {
  if (!attendee.responseIds && !attendee.viewedAt) {
    return ATTENDEE_STATUS.WAITING;
  }

  switch (activityType) {
    case activityTypes.poll:
      return attendee.responseIds?.length > 0
        ? ATTENDEE_STATUS.ANSWERED_CORRECT
        : ATTENDEE_STATUS.WAITING;

    case activityTypes.quiz: {
      if (!attendee.responseIds?.length) {
        return ATTENDEE_STATUS.WAITING;
      }

      // Check if all selected answers are correct
      const selectedOptions = options.filter((option) =>
        attendee.responseIds.includes(option._id),
      );
      const correctOptions = options.filter((option) => option.correct);

      const hasAllCorrect = correctOptions.every((option) =>
        attendee.responseIds.includes(option._id),
      );
      const hasOnlyCorrect = selectedOptions.every((option) => option.correct);

      return hasAllCorrect && hasOnlyCorrect
        ? ATTENDEE_STATUS.ANSWERED_CORRECT
        : ATTENDEE_STATUS.ANSWERED_INCORRECT;
    }

    case activityTypes.share:
      if (attendee.openedAt) {
        return ATTENDEE_STATUS.OPENED;
      }
      if (attendee.viewedAt) {
        return ATTENDEE_STATUS.VIEWED;
      }
      return ATTENDEE_STATUS.WAITING;

    default:
      return ATTENDEE_STATUS.WAITING;
  }
};

/**
 * Gets the response details for display in attendee card
 * @param {Object} attendee - The attendee object
 * @param {string} activityType - The type of activity
 * @param {Array} options - The activity options
 * @param {Object} activity - The full activity object (for Send activities)
 * @returns {Object} - Response details with text and timestamp
 */
export const getAttendeeResponseDetails = (
  attendee,
  activityType,
  options = [],
  activity,
) => {
  const status = getAttendeeStatus(attendee, activityType, options);

  switch (activityType) {
    case activityTypes.poll: {
      if (status === ATTENDEE_STATUS.WAITING) {
        return { text: "Waiting..", timestamp: "0:00" };
      }

      const selectedOption = options.find((option) =>
        attendee.responseIds?.includes(option._id),
      );
      return {
        text: selectedOption?.option || "Unknown",
        timestamp: attendee.responseTime || "0:00",
      };
    }

    case activityTypes.quiz: {
      if (status === ATTENDEE_STATUS.WAITING) {
        return { text: "Waiting..", timestamp: "0:00" };
      }

      const selectedOption = options.find((option) =>
        attendee.responseIds?.includes(option._id),
      );
      return {
        text: selectedOption?.option || "Unknown",
        timestamp: attendee.responseTime || "0:00",
      };
    }

    case activityTypes.share: {
      if (status === ATTENDEE_STATUS.WAITING) {
        return { text: "Waiting..", timestamp: "0:00" };
      }

      if (status === ATTENDEE_STATUS.OPENED) {
        return {
          text: "Opened Link",
          timestamp: attendee.openedTime || attendee.viewTime || "0:00",
        };
      }

      if (status === ATTENDEE_STATUS.VIEWED) {
        // Check if activity has links to determine response text
        const hasLinks =
          activity && activity.link && activity.link.trim() !== "";
        return {
          text: hasLinks ? "Viewed" : "Viewed Note",
          timestamp: attendee.viewTime || "0:00",
        };
      }

      // Fallback
      return { text: "Waiting..", timestamp: "0:00" };
    }

    default:
      return { text: "Waiting..", timestamp: "0:00" };
  }
};

/**
 * Filters attendees based on the selected filter
 * @param {Array} attendees - Array of attendee objects
 * @param {string} filter - The filter type
 * @param {string} activityType - The activity type
 * @param {Array} options - The activity options
 * @returns {Array} - Filtered attendees
 */
export const filterAttendees = (
  attendees,
  filter,
  activityType,
  options = [],
) => {
  if (filter === ATTENDEE_FILTERS.ALL) {
    return attendees;
  }

  return attendees.filter((attendee) => {
    const status = getAttendeeStatus(attendee, activityType, options);

    switch (filter) {
      case ATTENDEE_FILTERS.ANSWERED:
        return (
          status === ATTENDEE_STATUS.ANSWERED_CORRECT ||
          status === ATTENDEE_STATUS.OPENED
        );

      case ATTENDEE_FILTERS.VIEWED:
        return status === ATTENDEE_STATUS.VIEWED;

      case ATTENDEE_FILTERS.INCORRECT:
        return status === ATTENDEE_STATUS.ANSWERED_INCORRECT;

      case ATTENDEE_FILTERS.WAITING:
        return status === ATTENDEE_STATUS.WAITING;

      default:
        return true;
    }
  });
};

/**
 * Gets the filter configuration based on activity type
 * @param {string} activityType - The activity type
 * @param {Object} activity - The activity object (optional, for Send activities to check if has links)
 * @returns {Array} - Array of filter configurations
 */
export const getFilterConfig = (activityType, activity) => {
  const baseFilters = [{ key: ATTENDEE_FILTERS.ALL, label: "All", icon: null }];

  switch (activityType) {
    case activityTypes.poll:
      return [
        ...baseFilters,
        { key: ATTENDEE_FILTERS.ANSWERED, label: "", icon: "check" },
        { key: ATTENDEE_FILTERS.WAITING, label: "Waiting", icon: null },
      ];

    case activityTypes.quiz:
      return [
        ...baseFilters,
        { key: ATTENDEE_FILTERS.ANSWERED, label: "", icon: "check" },
        { key: ATTENDEE_FILTERS.INCORRECT, label: "", icon: "close" },
        { key: ATTENDEE_FILTERS.WAITING, label: "Waiting", icon: null },
      ];

    case activityTypes.share: {
      // Check if Share activity has links
      const hasLinks = activity && activity.link && activity.link.trim() !== "";

      if (hasLinks) {
        // Share with links: Show separate filters for viewed (yellow) and opened (green)
        return [
          ...baseFilters,
          { key: ATTENDEE_FILTERS.ANSWERED, label: "", icon: "check" },
          { key: ATTENDEE_FILTERS.VIEWED, label: "", icon: "check_yellow" },
          { key: ATTENDEE_FILTERS.WAITING, label: "Waiting", icon: null },
        ];
      } else {
        // Share without links: Just viewed (green) and waiting
        return [
          ...baseFilters,
          { key: ATTENDEE_FILTERS.ANSWERED, label: "", icon: "check" },
          { key: ATTENDEE_FILTERS.WAITING, label: "Waiting", icon: null },
        ];
      }
    }

    default:
      return baseFilters;
  }
};

/**
 * Formats timestamp for display
 * @param {string} timestamp - Raw timestamp
 * @returns {string} - Formatted timestamp (e.g., "0:25")
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp || timestamp === "0:00" || timestamp === 0) return "0:00";

  // Handle string format that's already formatted (e.g., "1:25")
  if (typeof timestamp === "string" && timestamp.includes(":")) {
    return timestamp;
  }

  // Convert seconds (number or string number) to mm:ss format
  const seconds =
    typeof timestamp === "number" ? timestamp : parseInt(timestamp, 10);
  if (isNaN(seconds)) return "0:00";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};
